import React, { useEffect, useState } from 'react';
import { Platform, ScrollView, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import MaterialIcons from 'react-native-vector-icons/MaterialIcons';
import { FontFamily } from '../../../../../constants/fonts';

export interface ColumnPickerOption {
  key: string;
  label: string;
}

export interface ColumnPickerGroup {
  key: string;
  label: string;
  options: ColumnPickerOption[];
}

interface GroupedColumnPickerPanelProps {
  groups: ColumnPickerGroup[];
  selectedKeys: string[];
  onChange: (keys: string[]) => void;
  defaultExpanded?: boolean;
  notice?: React.ReactNode;
}

// Web-only "click outside to close" helper — attach `ref` to a View wrapping BOTH the toggle
// button and the panel, so clicking the toggle button itself isn't treated as an outside click
// (which would otherwise re-open the panel right after this hook closes it).
export function useCloseOnOutsideClick(ref: React.RefObject<View>, onOutsideClick: () => void, enabled: boolean) {
  useEffect(() => {
    if (Platform.OS !== 'web' || !enabled) return;
    const handleOutsideClick = (event: MouseEvent) => {
      const node = ref.current as unknown as HTMLElement | null;
      if (node && !node.contains(event.target as Node)) onOutsideClick();
    };
    document.addEventListener('mousedown', handleOutsideClick, true);
    return () => document.removeEventListener('mousedown', handleOutsideClick, true);
  }, [ref, onOutsideClick, enabled]);
}

// Reusable "Table columns" panel: dimension columns are grouped (e.g. Location Hierarchy,
// Timeframe, Other Attributes) with per-group select/expand state. Metric columns are not
// part of this picker — they always display, per the panel's subtitle copy.
const GroupedColumnPickerPanel: React.FC<GroupedColumnPickerPanelProps> = ({ groups, selectedKeys, onChange, defaultExpanded = false, notice }) => {
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(() => new Set(defaultExpanded ? groups.map((group) => group.key) : []));


  const allKeys = groups.flatMap((group) => group.options.map((option) => option.key));
  const firstKey = allKeys[0];

  const selectAll = () => onChange(allKeys);
  const deselectAll = () => onChange(firstKey ? [firstKey] : []);
  const expandAll = () => setExpandedGroups(new Set(groups.map((group) => group.key)));
  const collapseAll = () => setExpandedGroups(new Set());

  const toggleGroupExpanded = (groupKey: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupKey)) next.delete(groupKey);
      else next.add(groupKey);
      return next;
    });
  };

  const toggleOption = (key: string) => {
    const hasKey = selectedKeys.includes(key);
    if (hasKey) {
      if (selectedKeys.length === 1) return;
      onChange(selectedKeys.filter((item) => item !== key));
    } else {
      onChange([...selectedKeys, key]);
    }
  };

  const toggleGroup = (group: ColumnPickerGroup) => {
    const groupKeys = group.options.map((option) => option.key);
    const allSelected = groupKeys.every((key) => selectedKeys.includes(key));
    if (allSelected) {
      const remaining = selectedKeys.filter((key) => !groupKeys.includes(key));
      onChange(remaining.length > 0 ? remaining : [firstKey]);
    } else {
      onChange([...new Set([...selectedKeys, ...groupKeys])]);
    }
  };

  return (
    <View style={styles.panel} testID="column-picker-panel">
      <Text style={styles.panelTitle}>Table columns</Text>
      <Text style={styles.panelSubtitle}>Choose the dimensions the table groups by. Metric columns always display.</Text>

      <View style={styles.actionsRow}>
        <TouchableOpacity onPress={selectAll}>
          <Text style={styles.actionLink}>Select All</Text>
        </TouchableOpacity>
        <Text style={styles.actionDivider}>|</Text>
        <TouchableOpacity onPress={deselectAll}>
          <Text style={styles.actionLink}>Deselect All</Text>
        </TouchableOpacity>
        <View style={styles.actionsSpacer} />
        <TouchableOpacity onPress={expandAll}>
          <Text style={styles.actionLink}>Expand All</Text>
        </TouchableOpacity>
        <Text style={styles.actionDivider}>|</Text>
        <TouchableOpacity onPress={collapseAll}>
          <Text style={styles.actionLink}>Collapse All</Text>
        </TouchableOpacity>
      </View>

      {notice}

      <ScrollView style={styles.groupsScroll} nestedScrollEnabled>
        {groups.map((group, index) => {
          const groupKeys = group.options.map((option) => option.key);
          const selectedInGroup = groupKeys.filter((key) => selectedKeys.includes(key)).length;
          const allSelected = selectedInGroup === groupKeys.length;
          const isExpanded = expandedGroups.has(group.key);
          return (
            <View key={group.key} style={[styles.groupBlock, index > 0 && styles.groupBlockDivider]}>
              <View style={styles.groupHeaderRow}>
                <TouchableOpacity
                  style={[styles.checkbox, allSelected && styles.checkboxChecked]}
                  onPress={() => toggleGroup(group)}
                >
                  {allSelected ? (
                    <Text style={styles.checkboxMark}>✓</Text>
                  ) : selectedInGroup > 0 ? (
                    <View style={styles.checkboxIndeterminateDash} />
                  ) : null}
                </TouchableOpacity>
                <Text style={styles.groupLabel}>{group.label}</Text>
                <Text style={styles.groupCount}>{`${selectedInGroup} of ${groupKeys.length}`}</Text>
                <TouchableOpacity style={styles.chevronHit} onPress={() => toggleGroupExpanded(group.key)}>
                  <MaterialIcons name={isExpanded ? 'expand-less' : 'expand-more'} size={20} color="#616161" />
                </TouchableOpacity>
              </View>

              {isExpanded && (
                <View style={styles.optionsGrid}>
                  {group.options.map((option) => {
                    const checked = selectedKeys.includes(option.key);
                    return (
                      <TouchableOpacity key={option.key} style={styles.option} onPress={() => toggleOption(option.key)}>
                        <View style={[styles.checkbox, checked && styles.checkboxChecked]}>
                          {checked ? <Text style={styles.checkboxMark}>✓</Text> : null}
                        </View>
                        <Text style={styles.optionLabel}>{option.label}</Text>
                      </TouchableOpacity>
                    );
                  })}
                </View>
              )}
            </View>
          );
        })}
      </ScrollView>

      <Text style={styles.footerNote}>
        Changes apply to the table immediately. At least one dimension must stay selected.
      </Text>
    </View>
  );
};

const styles = StyleSheet.create({
  panel: {
    position: 'absolute',
    top: 28,
    right: 0,
    width: 320,
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#E5E7EB',
    borderRadius: 10,
    padding: 12,
    zIndex: 1000,
    shadowColor: '#000000',
    shadowOpacity: 0.12,
    shadowOffset: { width: 0, height: 2 },
    shadowRadius: 8,
    elevation: 12,
  },
  panelTitle: {
    fontSize: 13,
    fontFamily: FontFamily.bold,
    fontWeight: '700',
    color: '#111827',
  },
  panelSubtitle: {
    marginTop: 2,
    fontSize: 11,
    fontFamily: FontFamily.regular,
    color: '#6B7280',
  },
  actionsRow: {
    marginTop: 10,
    paddingBottom: 8,
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  actionsSpacer: {
    flex: 1,
  },
  actionDivider: {
    fontSize: 11,
    color: '#D1D5DB',
  },
  actionLink: {
    fontSize: 11,
    fontFamily: FontFamily.medium,
    fontWeight: '600',
    color: '#0B5FC6',
  },
  groupsScroll: {
    marginTop: 4,
    maxHeight: 260,
  },
  groupBlock: {
    paddingVertical: 8,
  },
  groupBlockDivider: {
    borderTopWidth: 1,
    borderTopColor: '#E5E7EB',
  },
  groupHeaderRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  groupLabel: {
    fontSize: 11,
    fontFamily: FontFamily.bold,
    fontWeight: '700',
    color: '#111827',
    textTransform: 'uppercase',
    letterSpacing: 0.3,
  },
  groupCount: {
    fontSize: 11,
    fontFamily: FontFamily.regular,
    color: '#9CA3AF',
  },
  chevronHit: {
    marginLeft: 'auto',
    paddingLeft: 8,
  },
  optionsGrid: {
    marginTop: 8,
    paddingLeft: 21,
    flexDirection: 'row',
    flexWrap: 'wrap',
    rowGap: 8,
    columnGap: 10,
  },
  option: {
    width: '44%',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  optionLabel: {
    fontSize: 11,
    fontFamily: FontFamily.regular,
    color: '#374151',
    flexShrink: 1,
  },
  checkbox: {
    width: 16,
    height: 16,
    borderWidth: 1,
    borderColor: '#9CA3AF',
    borderRadius: 3,
    alignItems: 'center',
    justifyContent: 'center',
  },
  checkboxChecked: {
    backgroundColor: '#0B5FC6',
    borderColor: '#0B5FC6',
  },
  checkboxIndeterminateDash: {
    width: 8,
    height: 2,
    backgroundColor: '#0B5FC6',
  },
  checkboxMark: {
    fontSize: 10,
    color: '#FFFFFF',
    fontWeight: '700',
  },
  footerNote: {
    marginTop: 10,
    paddingTop: 8,
    borderTopWidth: 1,
    borderTopColor: '#E5E7EB',
    fontSize: 10,
    fontFamily: FontFamily.regular,
    color: '#9CA3AF',
  },
});

export default GroupedColumnPickerPanel;
